import { LanguagesEnum } from '@/api/enums';
import { equal, isIn } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { HubspotProperty } from '@/client/hubspot/types';
import { DashboardFilterValue } from '@/client/reports/types';
import {
  SCViewEnum,
  SecurityCultureSurvey,
  SurveyScheduleStatusEnum,
} from '@/client/security-culture/types';
import { Subjects } from '@/client/users';
import {
  LAST_CLOSED_SC_DEMO_OVERLAY,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { SC_COMMUNICATION_TEMPLATE_URL } from '@/common/constants/external-urls';
import { AppContext } from '@/common/context/AppContext';
import { BlockEmailsBanner } from '@/components/block-emails';
import {
  useSurveyReport,
  useSurveys,
} from '@/hooks/query/security-culture.hooks';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import i18n from '@/i18n';
import SCScheduledPageImg from '@/images/blank-pages/simplified-sc-scheduled-page.svg';
import { SCCategory } from '@/pages/security-culture/sc-dashboard-filterable/SCCategory';
import { SCFeedback } from '@/pages/security-culture/sc-dashboard-filterable/SCFeedback';
import { SCFilters } from '@/pages/security-culture/sc-dashboard-filterable/SCFilters';
import { SCReport } from '@/pages/security-culture/sc-dashboard-filterable/SCReport';
import { SCBlankPage } from '@/pages/security-culture/SCBlankPage';
import { selectCurrentAccount } from '@/store/features/account';
import { DateFormats } from '@/system-settings/enums';
import { BlankPage } from '@/ui/blank-page/BlankPage';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { BackButton } from '@/ui/styled-ui/BackButton';
import { isDemoModeActive } from '@/utils/demo-mode';
import { SCLockIcon, showBlockEmailsMessage } from '@/utils/helpers';
import {
  getCategoryTitle,
  getNavigationPath,
} from '@/utils/helpers/security-culture.helper';
import { parseFloatingNumber } from '@/utils/helpers/ui.helper';
import { hubspotTimestamp, hubspotTrack } from '@/utils/hubspot';
import { t } from 'i18next';
import moment from 'moment';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

const StyledOverlayContainer = styled(FlexContainer)`
  position: absolute;
  max-width: 780px;
  height: 100%;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  z-index: 9999;
  text-align: center;
  color: var(--white-main);

  p {
    line-height: 1.5;
  }

  svg {
    width: 67px;
    height: 85px;
  }
`;

const StyledIconWrapper = styled.div`
  margin-left: var(--small-padding);

  svg {
    width: 25px;
    height: 25px;
  }
`;

const StyledHeaderContainer = styled(FlexContainer)`
  &.is-demo {
    margin-bottom: var(--default-padding);
  }
`;

const pathItems = (account: Account) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('security.culture'),
    url: RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY](),
    template: AppBreadCrumbTemplate,
  },
];

type SCDashboardV2Props = {
  view: SCViewEnum;
};

export const SCDashboardV2: React.FC<SCDashboardV2Props> = ({ view }) => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { surveyId, categoryName } = useParams();
  const navigate = useNavigate();
  const { canUseFeature } = useFeatureFlag();
  const { state, triggerHasOverlay } = useContext(AppContext);
  const urlSurveyIds = useMemo(
    () => surveyId?.split(',').filter(Boolean),
    [surveyId],
  );

  const { isLoading, surveys } = useSurveys(
    {
      take: 1,
      skip: 0,
    },
    currentAccount?.id,
  );

  const { surveys: upcomingSurveys, isLoading: isUpcomingSurveysLoading } =
    useSurveys(
      {
        take: 1,
        skip: 0,
        sort: ['date,asc'],
        keepPreviousData: false,
        filters: [
          equal('isDemo', false),
          isIn('scheduleStatus', [SurveyScheduleStatusEnum.WAITING]),
        ],
      },
      currentAccount?.id,
    );

  const { surveys: sentSurveys, isLoading: isSentSurveysLoading } = useSurveys(
    {
      take: 1,
      skip: 0,
      keepPreviousData: false,
      filters: [
        equal('isDemo', false),
        isIn('scheduleStatus', [
          SurveyScheduleStatusEnum.SCHEDULED,
          SurveyScheduleStatusEnum.SENT,
        ]),
      ],
    },
    currentAccount?.id,
  );

  const canSeeEnhancedDemoReport = canUseFeature(
    Subjects.SECURITY_CULTURE_DEMO_REPORT,
  );

  const [overTimeParsed, setOverTimeParsed] = useState<{
    values: number[];
    labels: string[];
  }>({ values: [], labels: [] });

  const [filters, setFilters] = useState<DashboardFilterValue>({
    managers: [],
    departments: [],
    branches: [],
    groups: [],
  });

  const [selectedSurveys, setSelectedSurveys] =
    useState<SecurityCultureSurvey[]>();

  const {
    surveyReports,
    isLoading: areSurveyReportsLoading,
    refetch: refetchSurveyReports,
  } = useSurveyReport(
    {
      surveyId: selectedSurveys?.map((survey) => survey.id) as string[],
      branches: filters.branches?.map((branch) => branch?.id),
      groups: filters.groups?.map((group) => group?.id),
      departments: filters.departments?.map((department) => department?.name),
      managers: filters.managers?.map((manager) => manager?.name),
      enabled: view === SCViewEnum.ALL,
    },
    currentAccount?.id,
  );

  useEffect(() => {
    if (surveyReports?.scoredByPeriod?.length) {
      const dateMap = new Map<string, { totalScore: number; count: number }>();

      surveyReports.scoredByPeriod.forEach((item) => {
        const dateKey = moment(item.date).format('YYYY-MM-DD');
        const existing = dateMap.get(dateKey);

        if (existing) {
          existing.totalScore += item.surveyScore;
          existing.count += 1;
        } else {
          dateMap.set(dateKey, {
            totalScore: item.surveyScore,
            count: 1,
          });
        }
      });

      const sortedEntries = Array.from(dateMap.entries()).sort((a, b) => {
        return moment(a[0]).valueOf() - moment(b[0]).valueOf();
      });

      const overTimeParsed = sortedEntries.reduce(
        (object, [dateKey, { totalScore, count }]) => {
          const averageScore = totalScore / count;
          object.values.push(parseFloatingNumber(averageScore));
          object.labels.push(
            moment(dateKey)
              .locale(
                i18n.language === LanguagesEnum.DK
                  ? i18n.language
                  : LanguagesEnum.EN,
              )
              .format(DateFormats.SHORT_MONTH_YEAR),
          );
          return object;
        },
        { values: [] as number[], labels: [] as string[] },
      );

      setOverTimeParsed(overTimeParsed);
    } else if (selectedSurveys?.length) {
      const surveysReversed = selectedSurveys.slice().reverse();
      const overTimeParsed = surveysReversed.reduce(
        (object, survey) => {
          if (survey.score !== null && survey.score !== undefined) {
            object.values.push(parseFloatingNumber(survey.score));
            object.labels.push(
              moment(survey.date)
                .locale(
                  i18n.language === LanguagesEnum.DK
                    ? i18n.language
                    : LanguagesEnum.EN,
                )
                .format(DateFormats.SHORT_MONTH_YEAR),
            );
          }

          return object;
        },
        { values: [] as number[], labels: [] as string[] },
      );

      const hasDemo =
        selectedSurveys.some((survey) => survey.isDemo) &&
        selectedSurveys.length === 1;

      if (hasDemo && canSeeEnhancedDemoReport && selectedSurveys[0]) {
        overTimeParsed.values.unshift(...[50, 60]);
        overTimeParsed.labels.unshift(
          ...[
            moment(selectedSurveys[0].date)
              .add(-12, 'month')
              .locale(
                i18n.language === LanguagesEnum.DK
                  ? i18n.language
                  : LanguagesEnum.EN,
              )
              .format(DateFormats.SHORT_MONTH_YEAR),
            moment(selectedSurveys[0].date)
              .add(-6, 'month')
              .locale(
                i18n.language === LanguagesEnum.DK
                  ? i18n.language
                  : LanguagesEnum.EN,
              )
              .format(DateFormats.SHORT_MONTH_YEAR),
          ],
        );

        if (view === SCViewEnum.ALL) {
          const lastClosed = localStorage.getItem(LAST_CLOSED_SC_DEMO_OVERLAY);
          const showAfter = moment(lastClosed).add(24, 'hours');

          if (!lastClosed || moment().isAfter(showAfter)) {
            triggerHasOverlay && triggerHasOverlay(true);
          }
        }
      }

      setOverTimeParsed(overTimeParsed || { values: [], labels: [] });
    } else {
      setOverTimeParsed({ values: [], labels: [] });
    }

    hubspotTrack(HubspotProperty.VISITED_SECURITY_CULTURE, hubspotTimestamp());
  }, [surveyReports, selectedSurveys, canSeeEnhancedDemoReport, view]);

  // Refetch data when filters change
  useEffect(() => {
    if (!selectedSurveys?.length) return;

    refetchSurveyReports();
  }, [filters, selectedSurveys]);

  useEffect(() => {
    return () => {
      triggerHasOverlay && triggerHasOverlay(false);
    };
  }, []);

  useEffect(() => {
    if (!selectedSurveys?.length) {
      navigate(RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY](), {
        replace: true,
      });
      return;
    }

    const selectedSurveyIds = selectedSurveys
      .map((survey) => survey.id)
      .filter((id): id is string => Boolean(id));

    if (!selectedSurveyIds.length) return;

    const nextSurveyParam = selectedSurveyIds.join(',');
    const currentSurveyParam = urlSurveyIds?.join(',') || '';

    if (nextSurveyParam === currentSurveyParam) return;

    const targetPath = getNavigationPath(nextSurveyParam, view, categoryName);
    if (!targetPath) return;

    navigate(targetPath, { replace: true });
  }, [selectedSurveys, urlSurveyIds, view, categoryName]);

  const isDemo =
    canSeeEnhancedDemoReport &&
    selectedSurveys?.some((survey) => survey.isDemo) &&
    selectedSurveys?.length === 1;

  const overlayContent = () => (
    <StyledOverlayContainer
      direction="column"
      align="center"
      justify="center"
      gap={24}
    >
      <SCLockIcon />
      <h1>
        {t('security.culture.demo.overlay.heading.1')} <br />
        {t('security.culture.demo.overlay.heading.2')}
      </h1>
      <p
        dangerouslySetInnerHTML={{
          __html: t('security.culture.demo.overlay.paragraph'),
        }}
      />
      <strong>{t('security.culture.demo.overlay.suffix')}</strong>
      <AppButton
        className="mt-5"
        label={t('security.culture.continue.to.demo')}
        onClick={() => {
          localStorage.setItem(
            LAST_CLOSED_SC_DEMO_OVERLAY,
            moment().toISOString(),
          );
          triggerHasOverlay && triggerHasOverlay(false);
        }}
      />
    </StyledOverlayContainer>
  );

  const hasBlockEmailsBanner = showBlockEmailsMessage(currentAccount);

  const showLoading =
    (areSurveyReportsLoading &&
      isLoading &&
      !surveys &&
      !surveyReports &&
      view === SCViewEnum.ALL) ||
    (!isLoading &&
      view === SCViewEnum.ALL &&
      (isUpcomingSurveysLoading || isSentSurveysLoading));

  if (showLoading) {
    return (
      <FlexContainer direction="column" className="m-auto h-full">
        <ProgressSpinner />
        <h3>{t('security.culture.survey.loading')}</h3>
      </FlexContainer>
    );
  }

  const showScheduled =
    !isLoading &&
    !isUpcomingSurveysLoading &&
    !isSentSurveysLoading &&
    (upcomingSurveys?.count ?? 0) > 0 &&
    sentSurveys?.count === 0 &&
    view === SCViewEnum.ALL;

  if (showScheduled) {
    const scheduledDate =
      upcomingSurveys?.result?.[0]?.scheduledFor ??
      upcomingSurveys?.result?.[0]?.date;

    return (
      <>
        {hasBlockEmailsBanner && <BlockEmailsBanner />}
        <FlexContainer
          direction="column"
          align="flex-start"
          justify="flex-start"
          minHeight="100%"
        >
          <AppBreadCrumb model={pathItems(currentAccount as Account)} />
          <h1 className="mb-5">{t('security.culture')}</h1>
          <BlankPage
            image={SCScheduledPageImg}
            title={t('security.culture.scheduled.heading')}
            preDescription={
              scheduledDate
                ? t('security.culture.scheduled.send.date', {
                    date: moment(scheduledDate).format(DateFormats.DATEONLY),
                  })
                : undefined
            }
            description={t('security.culture.scheduled.description')}
            secondaryDescription={t('security.culture.scheduled.cta.text')}
            mainCtaText={t('security.culture.send.survey.find.template')}
            mainBookingLink={SC_COMMUNICATION_TEMPLATE_URL}
          />
        </FlexContainer>
      </>
    );
  }

  const hasOnlyDemoSurvey =
    surveys?.count === 1 && !!surveys?.result?.[0]?.isDemo;
  const showBlank =
    !isLoading &&
    ((hasOnlyDemoSurvey && !currentAccount?.isSystem) || !surveys?.count) &&
    !isDemoModeActive() &&
    view === SCViewEnum.ALL;

  if (showBlank) {
    return (
      <>
        {hasBlockEmailsBanner && <BlockEmailsBanner />}
        <SCBlankPage />
      </>
    );
  }

  return (
    <>
      {hasBlockEmailsBanner && <BlockEmailsBanner />}
      {state.hasOverlay && isDemo && overlayContent()}

      <FlexContainer
        direction="column"
        align="flex-start"
        justify="flex-start"
        minHeight="100%"
      >
        <AppBreadCrumb model={pathItems(currentAccount as Account)} />
        <StyledHeaderContainer
          justify="flex-start"
          gap={12}
          className={isDemo ? 'is-demo' : ''}
        >
          {view !== SCViewEnum.ALL && (
            <BackButton
              to={RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY](
                selectedSurveys?.map((survey) => survey.id).join(','),
              )}
            />
          )}
          <h1 className="m-0">{getCategoryTitle(view, categoryName, t)}</h1>
          {isDemo && (
            <>
              <Tooltip target=".demo-tooltip" position="right" />
              <StyledIconWrapper
                className="demo-tooltip"
                data-pr-tooltip={t('security.culture.demo.tooltip')}
              >
                <SCLockIcon />
              </StyledIconWrapper>
            </>
          )}
        </StyledHeaderContainer>
        <FlexContainer
          direction="column"
          justify="flex-start"
          align="flex-start"
          gap={24}
          className="flex-1"
        >
          <SCFilters
            selectedSurveys={selectedSurveys}
            urlSurveyIds={urlSurveyIds}
            filters={filters}
            setFilters={setFilters}
            setSelectedSurveys={setSelectedSurveys}
          />

          {view === SCViewEnum.ALL && (
            <SCReport
              surveyReports={surveyReports}
              areSurveyReportsLoading={areSurveyReportsLoading}
              overTimeParsed={overTimeParsed}
              selectedSurveys={selectedSurveys}
              isFilterApplied={Object.values(filters).some(
                (value) => value?.length > 0,
              )}
              isDemo={isDemo}
            />
          )}
          {view === SCViewEnum.FEEDBACK && (
            <SCFeedback filters={filters} selectedSurveys={selectedSurveys} />
          )}
          {view === SCViewEnum.CATEGORY && (
            <SCCategory filters={filters} selectedSurveys={selectedSurveys} />
          )}
        </FlexContainer>
      </FlexContainer>
    </>
  );
};
